import { HubSelect } from "@/components/HubSelect";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { Button } from "@bednbody/ui/components/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import {
  BriefcaseIcon,
  HandHeartIcon,
  MotorcycleIcon,
  PlusIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { StatTile } from "@/components/StatTile";
import { useAuth } from "@/lib/auth";
import { useHubsList } from "../ops-dashboard/ops-dashboard.hooks";
import { AddEmployeeModal } from "./components/AddEmployeeModal";
import { useRiderVehicles, useStaffPhotoUrls } from "../staff-media/staff-media.hooks";
import { RosterRowActions } from "./components/RosterRowActions";
import { RosterTableContainer } from "./components/RosterTableContainer";
import type { RosterPerson } from "./components/types";
import { useRosterStaff } from "./roster.hooks";
import { StaffDetailModal } from "./staff-detail/StaffDetailModal";
import { staffDetailAccess } from "./staff-detail/staff-detail.access";

const TABLE_HEADERS = ["Name", "Email", "Hub", "Status"];
const THERAPIST_HEADERS = ["Name", "Email", "Hub", "Code", "Status"];
const OFFICE_HEADERS = ["Name", "Email", "Role", "Hub", "Status"];

export function RosterPage() {
  const { claims } = useAuth();

  const viewerRole = claims?.role ?? null;
  // Who may change what lives in staffDetailAccess — the menu and the detail
  // modal read the same flags.
  const canManage = viewerRole === "admin" || viewerRole === "superadmin";

  const [hubFilter, setHubFilter] = useState<string | null>(claims?.hub_id ?? null);
  const { data: staff } = useRosterStaff(hubFilter);
  const { data: hubs } = useHubsList();

  const [addOpen, setAddOpen] = useState(false);
  // By id, so the modal shows the row as it is AFTER an edit refetches it.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => staff?.find((person) => person.id === selectedId) ?? null,
    [staff, selectedId],
  );

  const therapists = useMemo(
    () => (!staff ? undefined : staff.filter((s) => s.role === "therapist")),
    [staff],
  );
  const riders = useMemo(
    () => (!staff ? undefined : staff.filter((s) => s.role === "rider")),
    [staff],
  );
  // 0279 — faces and plates. Storage RLS decides which photos come back.
  const { data: photos } = useStaffPhotoUrls((staff ?? []).map((person) => person.id));
  const { data: vehicles } = useRiderVehicles();

  const office = useMemo(
    () => (!staff ? undefined : staff.filter((s) => s.role !== "therapist" && s.role !== "rider")),
    [staff],
  );

  const hubCount = useMemo(
    () => new Set((staff ?? []).map((person) => person.hubId).filter(Boolean)).size,
    [staff],
  );

  const openPerson = useCallback((person: RosterPerson) => setSelectedId(person.id), []);

  const actionCell = useCallback(
    (person: RosterPerson) => (
      <RosterRowActions
        access={staffDetailAccess(viewerRole, person)}
        onViewDetails={() => setSelectedId(person.id)}
        person={person}
        vehicle={vehicles?.get(person.id) ?? null}
      />
    ),
    [viewerRole, vehicles],
  );

  const tabs = [
    {
      value: "Therapists",
      people: therapists,
      description: "Click a therapist to see their profile, totals and bookings.",
      headers: THERAPIST_HEADERS,
      table: { showRole: false, showCode: true, showRank: "therapist" },
    },
    {
      value: "Riders",
      people: riders,
      description: "Click a rider to see their profile, plate, totals and trips.",
      headers: TABLE_HEADERS,
      table: { showRole: false, showPlate: true, showRank: "rider" },
    },
    {
      value: "Office & Management",
      people: office,
      description: "Hub managers and admin staff.",
      headers: OFFICE_HEADERS,
      table: { showRole: true },
    },
  ] as const;

  return (
    <div className="space-y-8">
      <PageHeader
        actions={
          canManage ? (
            <div className="flex items-center gap-2">
              <HubSelect
                ariaLabel="Hub"
                hubs={hubs ?? []}
                variant="combobox"
                value={hubFilter}
                onValueChange={setHubFilter}
              />
              <Button onClick={() => setAddOpen(true)}>
                <PlusIcon weight="bold" />
                Add Employee
              </Button>
            </div>
          ) : null
        }
        description={
          viewerRole === "hub_manager"
            ? "Everyone at your hub."
            : "Therapists, riders and office staff across your hubs."
        }
        title="Staff Roster"
      />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatTile
          detail={staff ? `Across ${hubCount} ${hubCount === 1 ? "hub" : "hubs"}` : "…"}
          icon={UsersThreeIcon}
          label="Total staff"
          value={staff?.length ?? "…"}
        />
        <StatTile
          detail={therapists ? activeLabel(therapists) : "…"}
          icon={HandHeartIcon}
          label="Therapists"
          value={therapists?.length ?? "…"}
        />
        <StatTile
          detail={riders ? activeLabel(riders) : "…"}
          icon={MotorcycleIcon}
          label="Riders"
          value={riders?.length ?? "…"}
        />
        <StatTile
          detail={office ? activeLabel(office) : "…"}
          icon={BriefcaseIcon}
          label="Office & management"
          value={office?.length ?? "…"}
        />
      </div>

      <Tabs className="gap-6" defaultValue="Therapists">
        <TabsList
          className="w-full min-w-0 max-w-full justify-start overflow-x-auto border-b border-border"
          variant="line"
        >
          {tabs.map((tab) => (
            <TabsTrigger className="flex-none px-3" key={tab.value} value={tab.value}>
              {tab.value}
              <span className="rounded-md bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                {tab.people?.length ?? "…"}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value}>
            <RosterTableContainer
              actionCell={actionCell}
              data={tab.people ?? []}
              loading={tab.people === undefined}
              onOpenPerson={openPerson}
              photos={photos}
              vehicles={vehicles}
              {...tab.table}
            />
          </TabsContent>
        ))}
      </Tabs>

      <StaffDetailModal
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        open={selected !== null}
        person={selected}
        photo={selected ? (photos?.get(selected.id) ?? null) : null}
        vehicle={selected ? (vehicles?.get(selected.id) ?? null) : null}
        viewerRole={viewerRole}
      />

      <AddEmployeeModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        defaultHubId={hubFilter}
      />
    </div>
  );
}

function activeLabel(people: readonly RosterPerson[]): string {
  return `${people.filter((person) => person.active).length} active`;
}
